import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Shield, CalendarRange, Users, LogOut, BookOpen, Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ROLES } from "@/lib/constants";

const NavItem = ({ to, icon: Icon, label, testid, compact }) => (
  <NavLink
    to={to}
    end
    data-testid={testid}
    title={label}
    className={({ isActive }) =>
      `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
        isActive ? "bg-sky-50 text-sky-700 border border-sky-200 font-medium" : "text-slate-600 hover:text-slate-900 hover:bg-slate-100 border border-transparent"
      }`
    }
  >
    <Icon className="w-4 h-4 shrink-0" /> <span className={compact ? "hidden xl:inline" : ""}>{label}</span>
  </NavLink>
);

const Brand = ({ compact }) => (
  <div className="flex items-center gap-3 min-w-0" data-testid="brand-block">
    <img src="/brand/eventpol-logo-192.png" alt="EventPOL · Gestión de eventos policiales" className="w-10 h-10 xl:w-12 xl:h-12 shrink-0 rounded-xl shadow-md" data-testid="brand-logo" />
    <div className={`leading-tight min-w-0 ${compact ? "hidden xl:block" : ""}`}>
      <div className="font-display font-bold text-sm text-slate-900">EventPOL</div>
      <div className="text-[0.625rem] text-violet-700">por Aprendendo</div>
      <div className="font-mono text-[0.5625rem] text-slate-500 tracking-widest mt-1 flex items-center gap-1"><Shield className="w-3 h-3" /> POLICÍAS DE GALICIA</div>
    </div>
  </div>
);

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  useEffect(() => setOpen(false), [loc.pathname]);
  const links = (compact) => (
    <>
      <NavItem to="/" icon={CalendarRange} label="Eventos" testid="nav-events-link" compact={compact} />
      {user?.role === "admin" && <NavItem to="/users" icon={Users} label="Usuarios" testid="nav-users-link" compact={compact} />}
      <NavItem to="/manual" icon={BookOpen} label="Manual" testid="nav-manual-link" compact={compact} />
    </>
  );
  const out = async () => { await logout(); nav("/login"); };
  return (
    <div className="min-h-screen lg:flex bg-slate-50 text-slate-900">
      {/* Ordenador: barra lateral (compacta hasta xl) */}
      <aside className="no-print hidden lg:flex w-[4.5rem] xl:w-60 shrink-0 bg-white border-r border-slate-200 flex-col p-3 gap-2 sticky top-0 h-screen">
        <div className="px-1 py-2 mb-3"><Brand compact /></div>
        {links(true)}
        <div className="mt-auto border-t border-slate-200 pt-3">
          <div className="hidden xl:block px-2 mb-2">
            <div className="text-sm font-medium truncate" data-testid="current-user-name">{user?.name}</div>
            <div className="font-mono text-[0.625rem] text-amber-700 tracking-widest uppercase" data-testid="current-user-role">{ROLES[user?.role]}</div>
          </div>
          <button data-testid="logout-button" onClick={out} title="Cerrar sesión"
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-slate-500 hover:text-red-700 hover:bg-red-50 transition-colors">
            <LogOut className="w-4 h-4" /> <span className="hidden xl:inline">Cerrar sesión</span>
          </button>
        </div>
      </aside>
      {/* Móvil y tableta: barra superior con menú desplegable */}
      <header className="no-print lg:hidden sticky top-0 z-[1100] bg-white/95 backdrop-blur border-b border-slate-200">
        <div className="flex items-center justify-between px-3 py-2">
          <Brand />
          <button onClick={() => setOpen((o) => !o)} className="p-2 rounded-lg border border-slate-200 text-slate-700" aria-label="Menú" data-testid="mobile-menu-button">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
        {open && (
          <nav className="px-3 pb-3 space-y-1 border-t border-slate-100 pt-2" data-testid="mobile-menu">
            {links(false)}
            <div className="flex items-center justify-between pt-2 mt-2 border-t border-slate-100">
              <span className="text-xs text-slate-500">{user?.name} · {ROLES[user?.role]}</span>
              <button onClick={out} className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-red-700 hover:bg-red-50"><LogOut className="w-4 h-4" /> Salir</button>
            </div>
          </nav>
        )}
      </header>
      <main className="flex-1 min-w-0">
        <Outlet />
      </main>
    </div>
  );
}
